import { AGENT_DETAIL_TABS, agentDetailPath, permissionGroupPath } from "./agent-detail.js";
import { deriveAgentsRoute } from "./agent-forms.js";
import { deriveApplicationsRoute } from "./applications.js";
import { resolvePortalRedirect } from "./portal-redirects.js";
import { derivePortalPage, deriveWorkspaceRoute } from "./portal-route.js";
import { SETTINGS_TABS } from "./settings-tabs.js";
import { titleCaseWords } from "./text-format.js";

export type ShellTone = "neutral" | "success" | "warning" | "danger";
/**
 * Workspace access states classified from a `GET /api/org/current` error.
 * Authentication is settled *before* any org-scoped call — the auth gate
 * renders the sign-in pages off `GET /api/me` — so this enum only carries
 * the workspace-level outcomes of an already-authenticated session:
 *
 * - `authenticated` — the workspace resolved; render the portal.
 * - `not_ready`     — the runtime exists but is not serving yet.
 * - `unavailable`   — any other failure loading the workspace.
 */
export type OrgAccessState = "authenticated" | "not_ready" | "unavailable";

export type NavItemId =
  | "dashboard"
  | "approvals"
  | "workspace"
  | "specialists"
  | "skills"
  | "automations"
  | "dashboards"
  | "applications"
  | "usage"
  | "account"
  | "settings"
  | "settings-people";

export type NavSectionId = "workspace" | "insights" | "admin";

export interface UiErrorLike {
  status?: number;
  errorCode?: string;
}

export interface GatewayStatusLike {
  status?: "online" | "offline";
  version?: string;
}

export interface GatewayShellState {
  status: "checking" | "online" | "offline" | "error";
  online: boolean;
  /**
   * Version reported by the connected Gateway when online. Surfaced so
   * footer-style components can read it from the shared shell state
   * rather than spawning a second `useGatewayStatusQuery` subscription
   * (which would override `PortalOrgProvider`'s SSE-aware polling toggle
   * and trigger redundant network polling).
   */
  version?: string;
}

export interface PortalBreadcrumb {
  href: string;
  label: string;
}

const NOT_READY_ERROR_CODES = new Set([
  "GATEWAY_NOT_FOUND",
  "GATEWAY_NOT_READY",
  "GATEWAY_PROVISIONING_FAILED",
]);

export interface ShellNavItemDefinition {
  id: NavItemId;
  label: string;
  href: string;
  section: NavSectionId;
  /**
   * Authority a viewer needs before the row appears at all. Declarative here so
   * the shell filters without hard-coding ids. `admin-or-branding` belongs to
   * Settings, whose tabs are individually gated: holding either authority means
   * at least one tab opens.
   */
  requires?: "admin" | "branding" | "admin-or-branding";
  /**
   * Reachable by route and by command-palette search, but not given a row in
   * the sidebar: the account menu already owns this personal utility, visited
   * rarely, so a rail row would only duplicate its entry point.
   */
  railHidden?: boolean;
}

export interface ShellNavSectionDefinition {
  id: NavSectionId;
  /**
   * Group heading in the rail. Omitted for the primary group, which needs no
   * label: it sits directly under the wordmark and its own items name it. A
   * "Workspace" heading would also collide with the Workspace item inside it.
   */
  label?: string;
}

export const SHELL_NAV_SECTIONS: readonly ShellNavSectionDefinition[] = [
  { id: "workspace" },
  { id: "insights", label: "Specialist outputs" },
  { id: "admin", label: "Administration" },
];

export const SHELL_NAV_ITEMS: readonly ShellNavItemDefinition[] = [
  { id: "dashboard", label: "Overview", href: "/", section: "workspace" },
  {
    id: "approvals",
    label: "Approvals",
    href: "/approvals/awaiting",
    section: "workspace",
  },
  { id: "workspace", label: "Workspace", href: "/workspace", section: "workspace" },
  { id: "specialists", label: "Specialists", href: "/specialists", section: "workspace" },
  { id: "skills", label: "Skills & Context", href: "/skills", section: "workspace" },
  {
    id: "account",
    label: "Your account",
    href: "/account",
    section: "workspace",
    railHidden: true,
  },
  { id: "automations", label: "Automations", href: "/automations", section: "insights" },
  { id: "dashboards", label: "Dashboards", href: "/dashboards", section: "insights" },
  { id: "applications", label: "Applications", href: "/applications", section: "insights" },
  {
    id: "settings",
    label: "Settings",
    href: "/settings",
    section: "admin",
    requires: "admin-or-branding",
  },
  {
    id: "settings-people",
    label: "People and roles",
    href: "/settings/people-and-roles",
    section: "admin",
    requires: "admin",
  },
  { id: "usage", label: "Usage", href: "/usage", section: "admin", requires: "admin" },
];

export const RUNTIME_OFFLINE_BANNER_MESSAGE = "Can't connect to specialists right now.";

const TOP_LEVEL_NAV_PREFIXES: Array<{ prefix: string; id: NavItemId }> = [
  { prefix: "/approvals", id: "approvals" },
  { prefix: "/workspace", id: "workspace" },
  { prefix: "/specialists", id: "specialists" },
  { prefix: "/skills", id: "skills" },
  { prefix: "/automations", id: "automations" },
  { prefix: "/dashboards", id: "dashboards" },
  { prefix: "/applications", id: "applications" },
  { prefix: "/usage", id: "usage" },
  { prefix: "/account", id: "account" },
  { prefix: "/settings/people-and-roles", id: "settings-people" },
  { prefix: "/settings", id: "settings" },
];

export function classifyOrgAccessState(error: UiErrorLike | undefined): OrgAccessState {
  if (!error) return "authenticated";
  if (error.errorCode && NOT_READY_ERROR_CODES.has(error.errorCode)) return "not_ready";
  return "unavailable";
}

export function deriveGatewayShellState(args: {
  pending?: boolean;
  data?: GatewayStatusLike;
  error?: UiErrorLike;
}): GatewayShellState {
  if (args.pending) {
    return { status: "checking", online: false };
  }
  if (args.error?.errorCode === "GATEWAY_OFFLINE") {
    return { status: "offline", online: false };
  }
  if (args.error) {
    return { status: "error", online: false };
  }
  if (args.data?.status === "online") {
    return {
      status: "online",
      online: true,
      ...(args.data.version ? { version: args.data.version } : {}),
    };
  }
  return { status: "offline", online: false };
}

export function deriveActiveNavItem(pathname: string): NavItemId | null {
  if (pathname === "/" || pathname === "") return "dashboard";
  for (const candidate of TOP_LEVEL_NAV_PREFIXES) {
    if (pathname === candidate.prefix || pathname.startsWith(`${candidate.prefix}/`)) {
      return candidate.id;
    }
  }
  return null;
}

/**
 * Fallback page identity label. Uses the active nav item's label
 * when the path maps to one; for a route that lives outside the sidebar nav
 * it title-cases the first path segment so the breadcrumb still names the page
 * instead of falling back to the generic "Workspace".
 */
export function deriveBreadcrumbLabel(pathname: string): string {
  const navId = deriveActiveNavItem(pathname);
  if (navId) {
    const item = SHELL_NAV_ITEMS.find((entry) => entry.id === navId);
    if (item) return item.label;
  }
  const firstSegment = pathname.split("/").filter(Boolean)[0];
  if (firstSegment) return titleCaseWords(firstSegment);
  return "Workspace";
}

const APPLICATIONS_BREADCRUMB = { href: "/applications", label: "Applications" };
const APPROVALS_BREADCRUMB = { href: "/approvals", label: "Approvals" };
const AUTOMATIONS_BREADCRUMB = { href: "/automations", label: "Automations" };
const DASHBOARDS_BREADCRUMB = { href: "/dashboards", label: "Dashboards" };
const PEOPLE_BREADCRUMB = {
  href: "/settings/people-and-roles",
  label: "People and roles",
};
const SKILLS_BREADCRUMB = { href: "/skills", label: "Skills" };
const SPECIALISTS_BREADCRUMB = { href: "/specialists", label: "Specialists" };
const WORKSPACE_BREADCRUMB = { href: "/workspace", label: "Workspace" };

const STATIC_PAGE_BREADCRUMBS: Readonly<Record<string, PortalBreadcrumb>> = {
  "/": { href: "/", label: "Overview" },
  "/account": { href: "/account", label: "Account" },
  "/applications": APPLICATIONS_BREADCRUMB,
  "/approvals": APPROVALS_BREADCRUMB,
  "/automations": AUTOMATIONS_BREADCRUMB,
  "/dashboards": DASHBOARDS_BREADCRUMB,
  "/settings/people-and-roles": PEOPLE_BREADCRUMB,
  "/skills": SKILLS_BREADCRUMB,
  "/specialists": SPECIALISTS_BREADCRUMB,
  "/usage": { href: "/usage", label: "Usage" },
  "/workspace": WORKSPACE_BREADCRUMB,
};

function pageNotFound(pathname: string): PortalBreadcrumb[] {
  return [{ href: pathname, label: "Page not found" }];
}

function decodedSegment(value: string | undefined): string | undefined {
  if (!value) return undefined;
  try {
    const decoded = decodeURIComponent(value).trim();
    return decoded || undefined;
  } catch {
    return undefined;
  }
}

function encodedSegment(value: string | undefined): string | undefined {
  const decoded = decodedSegment(value);
  return decoded === undefined ? undefined : encodeURIComponent(decoded);
}

function staticBreadcrumb(pathname: string): PortalBreadcrumb | undefined {
  return STATIC_PAGE_BREADCRUMBS[pathname.replace(/\/$/, "") || "/"];
}

function automationBreadcrumbs(pathname: string): PortalBreadcrumb[] | undefined {
  const segments = pathname.split("/").filter(Boolean);
  const automationId = encodedSegment(segments[1]);
  if (!automationId) return undefined;
  const automation = { href: `/automations/${automationId}`, label: "Automation" };
  if (segments.length === 2) return [AUTOMATIONS_BREADCRUMB, automation];
  const runId = encodedSegment(segments[3]);
  if (!runId) return undefined;
  return [
    AUTOMATIONS_BREADCRUMB,
    automation,
    { href: `/automations/${automationId}/runs/${runId}`, label: "Run" },
  ];
}

function dashboardBreadcrumbs(pathname: string): PortalBreadcrumb[] | undefined {
  if (pathname === "/dashboards" || pathname === "/dashboards/") {
    return [DASHBOARDS_BREADCRUMB];
  }
  return undefined;
}

function applicationBreadcrumbs(pathname: string): PortalBreadcrumb[] | undefined {
  if (pathname === "/applications" || pathname === "/applications/") {
    return [APPLICATIONS_BREADCRUMB];
  }
  if (pathname === "/applications/blueprints") {
    return [APPLICATIONS_BREADCRUMB, { href: "/applications/blueprints", label: "Blueprints" }];
  }
  const blueprintMatch = pathname.match(/^\/applications\/blueprints\/([^/]+)$/);
  const blueprintId = encodedSegment(blueprintMatch?.[1]);
  if (blueprintMatch) {
    if (!blueprintId) return undefined;
    return [
      APPLICATIONS_BREADCRUMB,
      { href: "/applications/blueprints", label: "Blueprints" },
      { href: `/applications/blueprints/${blueprintId}`, label: "Blueprint" },
    ];
  }
  const route = deriveApplicationsRoute(pathname);
  if (route.kind !== "external") return undefined;
  return [
    APPLICATIONS_BREADCRUMB,
    {
      href: `/applications/external/${encodeURIComponent(route.externalAppId)}`,
      label: "Application",
    },
  ];
}

function specialistBreadcrumbs(pathname: string): PortalBreadcrumb[] | undefined {
  if (pathname === "/specialists" || pathname === "/specialists/") {
    return [SPECIALISTS_BREADCRUMB];
  }
  if (pathname === "/specialists/new") {
    return [SPECIALISTS_BREADCRUMB, { href: pathname, label: "Choose a template" }];
  }
  if (pathname === "/specialists/new/configure") {
    return [SPECIALISTS_BREADCRUMB, { href: pathname, label: "Configure specialist" }];
  }

  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "specialists" || segments.length < 2 || segments.length > 4) {
    return undefined;
  }
  const agentId = decodedSegment(segments[1]);
  if (!agentId || agentId === "new") return undefined;
  let route: ReturnType<typeof deriveAgentsRoute>;
  try {
    route = deriveAgentsRoute(pathname);
  } catch {
    return undefined;
  }
  if (route.page !== "detail" && route.page !== "edit" && route.page !== "conversations") {
    return undefined;
  }

  const detailHref = agentDetailPath(agentId);
  const crumbs = [SPECIALISTS_BREADCRUMB, { href: detailHref, label: "Specialist" }];
  if (segments.length === 2) return crumbs;
  if (route.page === "edit" && segments.length === 3) {
    return [...crumbs, { href: `${detailHref}/edit`, label: "Edit" }];
  }
  if (route.page === "conversations" && segments.length === 3) {
    return [...crumbs, { href: `${detailHref}/conversations`, label: "Conversations" }];
  }
  if (route.page !== "detail") return undefined;

  const tab = AGENT_DETAIL_TABS.find((entry) => entry.id === segments[2]);
  if (!tab) return route.tab === "overview" ? crumbs : undefined;
  if (tab.id === "overview") return undefined;
  const tabHref = agentDetailPath(agentId, tab.id);
  const tabCrumbs = [...crumbs, { href: tabHref, label: tab.label }];
  if (tab.id === "browser" && segments[3] === "live")
    return [...tabCrumbs, { href: pathname, label: "Live view" }];
  if (segments.length === 3) return tabCrumbs;
  if (tab.id !== "permissions" || !route.permissionsGroup) return undefined;
  return [
    ...tabCrumbs,
    {
      href: permissionGroupPath(agentId, route.permissionsGroup),
      label: "Tool group",
    },
  ];
}

function workspaceBreadcrumbs(pathname: string): PortalBreadcrumb[] | undefined {
  if (pathname === "/workspace" || pathname === "/workspace/") {
    return [WORKSPACE_BREADCRUMB];
  }
  const segmentCount = pathname.split("/").filter(Boolean).length;
  const route = deriveWorkspaceRoute(pathname);
  if (route.startNew) {
    return segmentCount === 2
      ? [WORKSPACE_BREADCRUMB, { href: "/workspace/new", label: "New conversation" }]
      : undefined;
  }
  if (route.showList) {
    return segmentCount === 2
      ? [WORKSPACE_BREADCRUMB, { href: "/workspace/conversations", label: "Conversations" }]
      : undefined;
  }
  if (route.showArchived) {
    return segmentCount === 2
      ? [WORKSPACE_BREADCRUMB, { href: "/workspace/archived", label: "Archived conversations" }]
      : undefined;
  }
  if (!route.agentId) return undefined;
  const agentId = encodeURIComponent(route.agentId);
  const baseHref = `/workspace/${agentId}`;
  return [
    WORKSPACE_BREADCRUMB,
    route.sessionId
      ? {
          href: `${baseHref}/${encodeURIComponent(route.sessionId)}`,
          label: "Conversation",
        }
      : { href: baseHref, label: "New conversation" },
  ];
}

export function derivePortalBreadcrumbs(pathname: string): PortalBreadcrumb[] {
  const canonicalPathname = resolvePortalRedirect(pathname) ?? pathname;
  if (canonicalPathname === "/connectors/oauth/callback") {
    return [{ href: canonicalPathname, label: "Connecting account" }];
  }
  const settingsTab = SETTINGS_TABS.find(
    ({ href }) => href === canonicalPathname.replace(/\/$/, ""),
  );
  if (settingsTab) return [{ href: settingsTab.href, label: settingsTab.label }];
  const page = derivePortalPage(canonicalPathname);
  if (page === "not-found") {
    return pageNotFound(canonicalPathname);
  }

  const direct = staticBreadcrumb(canonicalPathname);
  if (direct) return [direct];

  if (page === "approvals") {
    const match = canonicalPathname.match(/^\/approvals\/parcel\/([^/]+)\/?$/);
    const parcelId = encodedSegment(match?.[1]);
    return match && parcelId
      ? [APPROVALS_BREADCRUMB, { href: `/approvals/parcel/${parcelId}`, label: "Approval" }]
      : [APPROVALS_BREADCRUMB];
  }
  if (page === "people-and-roles") {
    const match = canonicalPathname.match(/^\/settings\/people-and-roles\/([^/]+)$/);
    const userId = encodedSegment(match?.[1]);
    return match && userId
      ? [PEOPLE_BREADCRUMB, { href: `/settings/people-and-roles/${userId}`, label: "Person" }]
      : pageNotFound(canonicalPathname);
  }
  if (page === "skill-editor") {
    const match = canonicalPathname.match(/^\/skills\/([^/]+)$/);
    const skillId = encodedSegment(match?.[1]);
    if (!match || !skillId) return pageNotFound(canonicalPathname);
    return [
      SKILLS_BREADCRUMB,
      {
        href: `/skills/${skillId}`,
        label: decodedSegment(match[1]) === "new" ? "New skill" : "Skill",
      },
    ];
  }

  const breadcrumbs =
    page === "automations"
      ? automationBreadcrumbs(canonicalPathname)
      : page === "dashboards"
        ? dashboardBreadcrumbs(canonicalPathname)
        : page === "applications"
          ? applicationBreadcrumbs(canonicalPathname)
          : page === "specialists"
            ? specialistBreadcrumbs(canonicalPathname)
            : page === "workspace"
              ? workspaceBreadcrumbs(canonicalPathname)
              : undefined;
  return breadcrumbs ?? pageNotFound(canonicalPathname);
}
